import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyRefreshUserProfileQuery, useLoginMutation } from '../slices/usersApiSlice';
import { logout, setCredentials } from '../slices/authSlice';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import { trackUserLogin } from '../utils/analytics';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasValidatedExistingSession = useRef(false);

  const [login, { isLoading }] = useLoginMutation();
  const [refreshUserProfile] = useLazyRefreshUserProfileQuery();

  const { userInfo } = useSelector((state) => state.auth);
  const [isCheckingSession, setIsCheckingSession] = useState(Boolean(userInfo));
  const authIntent = searchParams.get('intent');
  const redirect = searchParams.get('redirect');

  const getPostAuthPath = () => {
    if (redirect) {
      return redirect;
    }

    if (authIntent === 'create-league' || authIntent === 'join-league') {
      return `/leagues?intent=${authIntent}`;
    }

    return null;
  };

  const buildAuthPath = (path) => {
    const nextParams = new URLSearchParams();

    if (authIntent) {
      nextParams.set('intent', authIntent);
    }

    if (redirect) {
      nextParams.set('redirect', redirect);
    }

    return `${path}${nextParams.toString() ? `?${nextParams.toString()}` : ''}`;
  };

  useEffect(() => {
    if (!userInfo) {
      setIsCheckingSession(false);
      return;
    }

    if (hasValidatedExistingSession.current) {
      return;
    }

    hasValidatedExistingSession.current = true;
    let isActive = true;

    const validateExistingSession = async () => {
      try {
        const refreshedUser = await refreshUserProfile().unwrap();

        if (!isActive) {
          return;
        }

        dispatch(setCredentials({ userInfo: refreshedUser }));
        navigate(getPostAuthPath() || refreshedUser.redirectPath || '/');
      } catch {
        if (!isActive) {
          return;
        }

        dispatch(logout());
        setIsCheckingSession(false);
      }
    };

    validateExistingSession();

    return () => {
      isActive = false;
    };
  }, [dispatch, navigate, refreshUserProfile, userInfo]);

  const submitHandler = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const submittedEmail = String(formData.get('email') || email).trim();
    const submittedPassword = String(formData.get('password') || password);

    if (!submittedEmail || !submittedPassword) {
      toast.error('Please enter your email and password');
      return;
    }

    try {
      const res = await login({ email: submittedEmail, password: submittedPassword }).unwrap();
      dispatch(setCredentials({ userInfo: res }));
      
      // Track successful login
      trackUserLogin(res._id);
      
      // Navigate immediately after login using the redirectPath from the server
      navigate(getPostAuthPath() || res.redirectPath || '/');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="form-modern fade-in text-center py-5">
                <Loader />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="form-modern fade-in">
              <div className="text-center mb-4">
                <h1 className="text-primary mb-2">Welcome Back</h1>
                <p className="text-secondary">Sign in to your account</p>
              </div>

              <Form onSubmit={submitHandler}>
                <div className="form-group-modern">
                  <label htmlFor="email" className="form-label-modern">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input-modern"
                    placeholder="Enter your email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group-modern">
                  <label htmlFor="password" className="form-label-modern">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="form-input-modern"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="text-end mb-3">
                  <Link
                    to={buildAuthPath('/forgot-password')}
                    className="text-decoration-none fw-medium"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  disabled={isLoading}
                  type="submit"
                  className="btn btn-primary-modern w-100 mb-3"
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </button>
              </Form>

              {isLoading && (
                <div className="text-center py-3">
                  <Loader />
                </div>
              )}

              <div
                className="text-center mt-4 pt-4"
                style={{ borderTop: '1px solid var(--border-light)' }}
              >
                <p className="text-secondary mb-0">
                  New to League Runner?{' '}
                  <Link
                    to={buildAuthPath('/register')}
                    className="text-decoration-none fw-medium"
                  >
                    Create an account
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
